/**
 * Pixen Intrigue is a faerie-court intrigue RPG built on SRD 5.2.1 (uses the
 * typed 5e store today). A future option is to model its bespoke fae/intrigue
 * content via the system_entity (JSONB, config-driven) method on the api —
 * DECISION PENDING; defaultSystem stays 'srd-5-2-1' for now.
 */
import type { WorldConfig } from '@gamecodex/world-config'
// Shared shell baseline — NOT generic: it is Grymwyrd's own literal copy (see
// packages/@gamecodex/world-ui/src/content/en/*.ts). `pixEn` (this world's own
// courtly-fae overrides, src/content/en.ts) MUST be spread after baseEn below,
// or Grymwyrd's hero text renders verbatim on pixenintrigue.com — which is what
// happened in prod.
import { baseEn } from '@/content/en'
import { pixEn } from './content/en'

export const PIXEN_INTRIGUE_SYSTEM_ID = 'srd-5-2-1'

export const pixenIntrigueWorldConfig: WorldConfig = {
  worldId: 'pixenintrigue',
  worldName: 'Pixen Intrigue',
  legalContentName: 'Our Pixen Intrigue Content',
  domain: 'pixenintrigue.com',

  locales: {
    default: 'en',
    supported: [{ code: 'en', label: 'English', native: 'English', dir: 'ltr' }],
  },
  content: {
    en: { 'common.worldName': 'Pixen Intrigue', ...baseEn, ...pixEn },
  },
  branding: {
    images: {
      logo: '/apple-touch-icon.png',
      ogImage: '/og/default.png',
      favicon: '/favicon.png',
      avatarFallback: '/avatars/default.svg',
      parchmentTexture: '/textures/parchment-dark.png',
      splatterTexture: '/textures/blood-splatter.png',
    },
    fonts: {
      display: 'Cinzel Decorative',
      heading: 'Cinzel',
      body: 'EB Garamond',
      stylesheetUrl:
        'https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700&family=Cinzel+Decorative:wght@400;700;900&family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400&family=EB+Garamond:ital,wght@0,400;0,500;1,400&family=Inter:wght@400;500;600&display=swap',
    },
    background: { variant: 'grimdark', params: { particleColor: '#9b3fb5', fogColor: '#0b0913' } },
  },

  gameSystems: [
    { id: 'srd-5-2-1', label: 'SRD 5.2.1', available: true },
    { id: 'pixen-intrigue', label: 'Pixen Intrigue' },
  ],
  // The api's pub_setting slug for the Pixen Intrigue books is `montkelian`
  // (the Montkelian setting: Pixen Intrigue, Philodendron Fort, the Kyp novels).
  // The facet id is sent verbatim as `?setting=` and matched against each row's
  // `provenance.setting_slugs`, so it MUST be the DB slug — `pixenintrigue`
  // returned an honest empty bestiary in prod.
  // api `pub_setting` "Montkelian" — scopes book listings to this world's own shelf.
  settingId: 'a0f48ec5-5454-425b-b625-2ae1c8363b26',
  settings: [{ id: 'montkelian', label: 'Montkelian', available: true }],
  defaultSystem: PIXEN_INTRIGUE_SYSTEM_ID,

  // The live "Pixen Intrigue — Hex Tile Kit" (7 hex-terrain-tile GLBs: blank,
  // dead-end, straight, bend, T-junction, crossing, open-room) on the Pixen
  // Intrigue book. Powers `/map-builder`.
  mapBuilder: { kitId: '02a04bb9-c399-4faf-a5b4-a2bbf54c071a' },

  homeCampaign: {
    slug: 'pixen-intrigue',
    eyebrow: 'A Game of Fae Courts & Secrets',
    title: 'Pixen Intrigue',
    body: 'Navigate the glittering, treacherous courts of the fae. Trade secrets, forge alliances, and outmaneuver rivals where every whisper is a weapon.',
    ctaLabel: 'Enter the Court',
  },

  seo: {
    defaultDescription:
      'A tabletop RPG of faerie-court intrigue and social scheming, powered by SRD 5.2.1. Trade in secrets and survive the courts of the fae.',
    tagline: 'Every Whisper a Weapon.',
    defaultOgImage: '/og/default.png',
  },

  openCanonSystemIds: ['srd-5-2-1'],
  lockedSettingIds: ['montkelian'],
  unlockBookIds: [],
  fallbackUnlockBookId: '',
}
